/**
 * Çoklu etiketi sayfalara dizme (imposition).
 *
 * Rulo modunda her etiket kendi "sayfası"dır (yazıcı tek tek besler).
 * Sayfa modunda etiketler A4 üzerine grid halinde yerleşir.
 *
 * `startOffset`: yarım kullanılmış etiket sayfası için ilk N gözü boş bırakır.
 * Pratikte en çok ihtiyaç duyulan ayar — bu olmadan her baskıda yeni sayfa
 * harcanır.
 */

import { LabelTemplate, PlaceholderData, getDesignSize, A4_WIDTH_MM, A4_HEIGHT_MM } from '../../../types/labelTemplate.types';

/** Basılacak tek bir etiket örneği */
export interface LabelInstance {
  id: string;
  data: PlaceholderData;
}

export interface PlacedLabel {
  instance: LabelInstance;
  /** Sayfa içindeki sol üst köşe konumu (mm) */
  xMm: number;
  yMm: number;
  row: number;
  column: number;
}

export interface LabelPage {
  index: number;
  widthMm: number;
  heightMm: number;
  labels: PlacedLabel[];
}

export interface ImpositionOptions {
  /** Sayfa modunda baştan atlanacak göz sayısı */
  startOffset?: number;
}

/** Kaynak listeden adet bilgisine göre tekrarlı örnek listesi üretir */
export function expandInstances(
  rows: Array<{ id: string; data: PlaceholderData; quantity: number }>,
): LabelInstance[] {
  const out: LabelInstance[] = [];
  rows.forEach((row) => {
    const q = Math.max(0, Math.floor(row.quantity));
    for (let i = 0; i < q; i++) {
      out.push({ id: `${row.id}#${i}`, data: row.data });
    }
  });
  return out;
}

function pageDimensions(t: LabelTemplate): { widthMm: number; heightMm: number } {
  const { layout } = t;
  if (layout.mode === 'roll') {
    // Rulo: sayfa = tek etiketin fiziksel ölçüsü
    return { widthMm: t.labelWidthMm, heightMm: t.labelLengthMm };
  }
  if (layout.pageSize === 'A4') return { widthMm: A4_WIDTH_MM, heightMm: A4_HEIGHT_MM };
  if (layout.pageSize === 'A5') return { widthMm: 148, heightMm: 210 };
  return { widthMm: layout.pageWidthMm ?? A4_WIDTH_MM, heightMm: layout.pageHeightMm ?? A4_HEIGHT_MM };
}

export function imposeLabels(
  instances: LabelInstance[],
  template: LabelTemplate,
  opts?: ImpositionOptions,
): LabelPage[] {
  const page = pageDimensions(template);
  const { layout } = template;

  if (layout.mode === 'roll') {
    return instances.map((instance, i) => ({
      index: i,
      widthMm: page.widthMm,
      heightMm: page.heightMm,
      labels: [{ instance, xMm: 0, yMm: 0, row: 0, column: 0 }],
    }));
  }

  // Sayfa modunda göze yerleşen etiketin ölçüsü fiziksel etiket ölçüsüdür
  const cellW = template.labelWidthMm;
  const cellH = template.labelLengthMm;
  const perPage = Math.max(1, layout.columns * layout.rows);

  const offset = Math.max(0, Math.floor(opts?.startOffset ?? 0)) % perPage;
  const pages: LabelPage[] = [];

  let cursor = offset;
  let pageIndex = 0;
  let current: LabelPage = { index: 0, widthMm: page.widthMm, heightMm: page.heightMm, labels: [] };

  instances.forEach((instance) => {
    if (cursor >= perPage) {
      pages.push(current);
      pageIndex += 1;
      current = { index: pageIndex, widthMm: page.widthMm, heightMm: page.heightMm, labels: [] };
      cursor = 0;
    }

    const row = Math.floor(cursor / layout.columns);
    const column = cursor % layout.columns;

    current.labels.push({
      instance,
      xMm: layout.marginLeftMm + column * (cellW + layout.gapXMm),
      yMm: layout.marginTopMm + row * (cellH + layout.gapYMm),
      row,
      column,
    });

    cursor += 1;
  });

  if (current.labels.length > 0) pages.push(current);
  return pages;
}

/** Yerleşimin sayfaya sığıp sığmadığını kontrol eder */
export function validateLayout(template: LabelTemplate): string[] {
  const warnings: string[] = [];
  const { layout } = template;
  if (layout.mode !== 'sheet') return warnings;

  const page = pageDimensions(template);
  const neededW =
    layout.marginLeftMm +
    layout.columns * template.labelWidthMm +
    (layout.columns - 1) * layout.gapXMm +
    layout.marginRightMm;
  const neededH =
    layout.marginTopMm +
    layout.rows * template.labelLengthMm +
    (layout.rows - 1) * layout.gapYMm +
    layout.marginBottomMm;

  if (neededW > page.widthMm + 0.5) {
    warnings.push(
      `Yerleşim sayfa genişliğini aşıyor: ${neededW.toFixed(1)} mm gerekli, ${page.widthMm} mm var. Kolon sayısını veya boşlukları azaltın.`,
    );
  }
  if (neededH > page.heightMm + 0.5) {
    warnings.push(
      `Yerleşim sayfa yüksekliğini aşıyor: ${neededH.toFixed(1)} mm gerekli, ${page.heightMm} mm var. Satır sayısını veya boşlukları azaltın.`,
    );
  }

  const design = getDesignSize(template);
  const overflowing = template.elements.filter(
    (el) => el.xMm + el.widthMm > design.widthMm + 0.5 || el.yMm + el.heightMm > design.heightMm + 0.5,
  );
  if (overflowing.length > 0) {
    warnings.push(`${overflowing.length} eleman etiket sınırının dışına taşıyor; baskıda kesilecek.`);
  }

  return warnings;
}

/** Sayfa modunda bir sayfaya kaç etiket sığdığı */
export function labelsPerPage(template: LabelTemplate): number {
  if (template.layout.mode === 'roll') return 1;
  return Math.max(1, template.layout.columns * template.layout.rows);
}
